import {
  type CascoCampaign,
  type GeneratorKey,
  leadGenerationCampaignsPartialUpdateMutation,
  leadGenerationCampaignsRetrieveQueryKey
} from '@crm/api'
import {
  parseRunTime,
  RUN_TIME_FORMAT,
  toApiRunTime
} from '@crm/features/lead-generation/utils'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  notification,
  Row,
  Switch,
  TimePicker
} from 'antd'
import { useEffect } from 'react'

import { GeneratorTag } from '../GeneratorTag/GeneratorTag'

import type { Dayjs } from 'dayjs'

const FORM_ID = 'lead-generation-config-form'

type FormValues = {
  name: string
  is_active: boolean
  run_time?: Dayjs | null
}

type Props = {
  campaign?: CascoCampaign
  generatorKey: GeneratorKey
  isLoading?: boolean
}

export const CampaignConfigCard = ({
  campaign,
  generatorKey,
  isLoading
}: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm<FormValues>()

  const { invalidateQueries } = useInvalidatePredicateQueries()

  useEffect(() => {
    if (campaign) {
      form.setFieldsValue({
        name: campaign.name,
        is_active: campaign.is_active,
        run_time: parseRunTime(campaign.run_time)
      })
    }
  }, [campaign, form])

  const { mutate, isPending } = useMutation({
    ...leadGenerationCampaignsPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([
        leadGenerationCampaignsRetrieveQueryKey({
          path: { generator_key: generatorKey }
        })
      ])

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const onSubmit = ({ name, is_active, run_time }: FormValues) =>
    mutate({
      path: { generator_key: generatorKey },
      body: { name, is_active, run_time: toApiRunTime(run_time) }
    })

  return (
    <Card
      loading={isLoading}
      title={t('lead_generation.campaign_config')}
      extra={
        <Button htmlType="submit" form={FORM_ID} loading={isPending}>
          {t('buttons.save')}
        </Button>
      }
    >
      <Form
        id={FORM_ID}
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        requiredMark={false}
      >
        <Row gutter={[18, 0]}>
          <Col span={24} md={12}>
            <Form.Item
              name="name"
              label={t('lead_generation.name')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="is_active"
              label={t('lead_generation.status')}
              valuePropName="checked"
            >
              <Switch
                checkedChildren={t('lead_generation.active')}
                unCheckedChildren={t('lead_generation.inactive')}
              />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item label={t('lead_generation.generator')}>
              <GeneratorTag generatorKey={generatorKey} />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="run_time"
              label={t('lead_generation.daily_run_time')}
            >
              <TimePicker format={RUN_TIME_FORMAT} className="w-full" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  )
}
